import type { PostoDoVeiculo } from '../../dominio/didatico/configuracaoVeiculo'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { girarEmTorno, limitar, paraArray, somar, vetor, type Vetor3 } from '../../dominio/geometria/vetor3'
import { DISTANCIA_MAXIMA, DISTANCIA_MINIMA, type Vetor } from '../camera/comandosCamera'
import { dadosDaPorta, type IdPortaOficina } from './zonasOperacaoPorta'
import { zonaOperacaoTampaPorId } from './zonasOperacaoTampa'
import type { IdLadoTerceiraPorta } from './zonasOperacaoTerceiraPorta'

/** Peça que a oficina está trabalhando, para a câmera saber o que olhar. */
export type AlvoOficina =
  | { peca: 'porta'; idPorta: IdPortaOficina }
  | { peca: 'terceiraPorta'; lado: IdLadoTerceiraPorta }
  | { peca: 'tampaTraseira' }

export interface EnquadramentoOficina {
  posicao: Vetor
  alvo: Vetor
}

/**
 * Três quartos da peça, não a vista de frente para ela.
 *
 * "Enquadrar a seleção" mantém a direção de onde a câmera já olhava, e quem
 * chega à oficina vem de uma pose calibrada para o veículo inteiro: o
 * resultado era a chapa da porta preenchendo a tela de frente, sem
 * profundidade — nada indicava a espessura da porta, onde ficam as dobradiças
 * nem para que lado a peça abre. O desvio lateral e a elevação abaixo existem
 * para devolver essa leitura: a face da peça continua sendo o assunto, mas
 * agora com a borda dianteira e o volume da carroceria visíveis ao lado.
 */
const DESVIO_RAD = (38 * Math.PI) / 180
const ELEVACAO_RAD = (20 * Math.PI) / 180

/** Direção de observação a partir da normal da peça, já com o desvio e a elevação. */
function direcaoDeObservacao(normal: Vetor3, desvioRad: number): Vetor3 {
  const desviada = girarEmTorno(normal, vetor(0, 1, 0), desvioRad)
  const cos = Math.cos(ELEVACAO_RAD)
  return vetor(desviada.x * cos, Math.sin(ELEVACAO_RAD), desviada.z * cos)
}

/**
 * Aplica ao ponto a mesma composição que a cena usa para apoiar o veículo
 * tombado ou capotado (Euler XYZ do three.js: rolagem em Z antes da
 * inclinação em X), e sobe o conjunto pela altura de apoio — sem isso a pose
 * calculada no referencial do veículo apontaria para o vazio nessas duas
 * configurações.
 */
function aplicarPosto(ponto: Vetor3, posto: PostoDoVeiculo): Vetor3 {
  const girado = girarEmTorno(ponto, vetor(0, 0, 1), posto.rolagemRad)
  const inclinado = girarEmTorno(girado, vetor(1, 0, 0), posto.inclinacaoLongitudinalRad)
  return vetor(inclinado.x, inclinado.y + posto.alturaApoio, inclinado.z)
}

/** Centro da peça e distância de observação, no referencial do veículo. */
function alvoNoVeiculo(
  perfil: PerfilVeiculo,
  alvo: AlvoOficina,
): { centro: Vetor3; normal: Vetor3; desvioRad: number; distancia: number } {
  if (alvo.peca === 'tampaTraseira') {
    const chapa = zonaOperacaoTampaPorId(perfil, 'chapa')
    const centro = chapa
      ? vetor(chapa.posicao[0], chapa.posicao[1], chapa.posicao[2])
      : vetor(0, perfil.cinturaY, perfil.traseiraZ + 0.1)
    return {
      centro,
      // de trás do veículo, desviando para o lado do motorista
      normal: vetor(0, 0, -1),
      desvioRad: -DESVIO_RAD,
      distancia: perfil.largura * 1.75,
    }
  }

  const { lado, faixa } = alvo.peca === 'porta'
    ? dadosDaPorta(perfil, alvo.idPorta)
    : { lado: alvo.lado, faixa: perfil.portaTraseira }
  const sinal = lado === 'esquerdo' ? 1 : -1
  const comprimentoPeca = Math.abs(faixa.zDianteiro - faixa.zTraseiro)

  return {
    centro: vetor(
      sinal * (perfil.largura / 2),
      (perfil.topoSoleiraY + perfil.cinturaY) / 2 + 0.05,
      (faixa.zDianteiro + faixa.zTraseiro) / 2,
    ),
    normal: vetor(sinal, 0, 0),
    // desvia sempre para a dianteira: é de lá que se vê a borda da dobradiça
    desvioRad: -sinal * DESVIO_RAD,
    distancia: comprimentoPeca * 2.3,
  }
}

/**
 * Pose de observação da peça em trabalho na oficina de corte.
 *
 * É absoluta — não parte de onde a câmera estava —, para que trocar de peça
 * ou voltar ao módulo sempre chegue ao mesmo ângulo reconhecível.
 */
export function enquadramentoOficina(
  perfil: PerfilVeiculo,
  alvo: AlvoOficina,
  posto: PostoDoVeiculo,
): EnquadramentoOficina {
  const { centro, normal, desvioRad, distancia } = alvoNoVeiculo(perfil, alvo)
  const raio = limitar(distancia, DISTANCIA_MINIMA, DISTANCIA_MAXIMA)
  const direcao = direcaoDeObservacao(normal, desvioRad)
  const posicao = somar(centro, vetor(direcao.x * raio, direcao.y * raio, direcao.z * raio))

  return {
    posicao: paraArray(aplicarPosto(posicao, posto)),
    alvo: paraArray(aplicarPosto(centro, posto)),
  }
}

/**
 * Enquadra uma ação concreta da demonstração. A posição vem das mesmas zonas
 * usadas pela ferramenta e pelo halo, para os três indicadores falarem do
 * mesmo ponto da peça.
 */
export function enquadramentoZonaOficina(
  perfil: PerfilVeiculo,
  alvo: AlvoOficina,
  posto: PostoDoVeiculo,
  zona: readonly [number, number, number],
): EnquadramentoOficina {
  const sinalLateral = alvo.peca === 'tampaTraseira'
    ? 0
    : alvo.peca === 'porta'
      ? (dadosDaPorta(perfil, alvo.idPorta).lado === 'esquerdo' ? 1 : -1)
      : (alvo.lado === 'esquerdo' ? 1 : -1)
  const direcao = alvo.peca === 'tampaTraseira'
    ? vetor(0.55, 0.4, -0.73)
    : direcaoDeObservacao(vetor(sinalLateral, 0, 0), -sinalLateral * DESVIO_RAD)
  const distancia = limitar(perfil.largura * 0.9, DISTANCIA_MINIMA, DISTANCIA_MAXIMA)
  const centro = vetor(zona[0], zona[1], zona[2])
  const posicao = somar(centro, vetor(direcao.x * distancia, direcao.y * distancia, direcao.z * distancia))
  return { posicao: paraArray(aplicarPosto(posicao, posto)), alvo: paraArray(aplicarPosto(centro, posto)) }
}
